/**
 * User Types
 * Core user data structure and role definitions
 */

// ==================== User Roles ====================

export const USER_ROLES = {
  GUEST: 0, // Guest (unauthenticated or limited access)
  USER: 1, // Regular user (default for authenticated users)
  MODERATOR: 2, // Moderator (can manage content/users)
  EDITOR: 3, // Editor (can edit content)
  ADMIN: 4, // Administrator (full access)
  OWNER: 5, // Owner (highest privilege)
} as const

// Type for role values
export type UserRole = (typeof USER_ROLES)[keyof typeof USER_ROLES]

// Role name mapping
export const ROLE_NAMES: Record<UserRole, string> = {
  [USER_ROLES.GUEST]: 'Guest',
  [USER_ROLES.USER]: 'User',
  [USER_ROLES.MODERATOR]: 'Moderator',
  [USER_ROLES.EDITOR]: 'Editor',
  [USER_ROLES.ADMIN]: 'Admin',
  [USER_ROLES.OWNER]: 'Owner',
}

// ==================== User Interface ====================

export interface User {
  id: string
  name?: string | null // Display name
  phone?: string | null // Phone number (optional - can log in with email)
  email?: string | null // Email address (optional - can log in with phone)
  profilePictureUrl?: string | null // Profile picture URL from storage
  role: number // User role (1=User, 2=Moderator, 3=Editor, 4=Admin, 5=Owner)
  isNewUser?: boolean // Flag indicating if this is the user's first login
  deletedAt?: string | null // Soft delete timestamp (30-day grace period)
  suspendedAt?: string | null // Account suspension timestamp
  suspendedReason?: string | null // Reason for suspension
  suspendedBy?: string | null // Admin who suspended account
  lastLoginAt?: string // Last successful login timestamp
  createdAt: string
  updatedAt: string // Auto-updated on changes (always included)
}

// ==================== User API Types ====================

// Phone Update
export interface SendPhoneOTPRequest {
  phone: string
}

export interface SendPhoneOTPResponse {
  success: boolean
  message: string
}

export interface VerifyPhoneOTPRequest {
  phone: string
  otp: string
}

export interface VerifyPhoneOTPResponse {
  success: boolean
  message: string
  data: {
    user: User
  }
}

// Email Update
export interface SendEmailOTPRequest {
  email: string
}

export interface SendEmailOTPResponse {
  success: boolean
  message: string
}

export interface VerifyEmailOTPRequest {
  email: string
  otp: string
}

export interface VerifyEmailOTPResponse {
  success: boolean
  message: string
  data: {
    user: User
  }
}

// Account Deletion
// Note: DeleteAccountRequest is no longer needed since we don't send phone in body
// Keeping the type for backwards compatibility, but it's not used in API calls
export interface DeleteAccountRequest {
  phone: string
}

export interface DeleteAccountResponse {
  success: boolean
  message: string
}

// Name Update
export interface UpdateNameRequest {
  name: string
}

export interface UpdateNameResponse {
  success: boolean
  message: string
  data: {
    user: User
  }
}

// Profile Picture Update
export interface UpdateProfilePictureRequest {
  profilePictureUrl: string
}

export interface UpdateProfilePictureResponse {
  success: boolean
  message: string
  data: {
    user: User
  }
}

// Profile Picture Removal
export interface RemoveProfilePictureResponse {
  success: boolean
  message: string
  data: {
    user: User
  }
}

// Name Removal
export interface RemoveNameResponse {
  success: boolean
  message: string
  data: {
    user: User
  }
}

// Email Removal
export interface RemoveEmailResponse {
  success: boolean
  message: string
  data: {
    user: User
  }
}

// Phone Removal
export interface RemovePhoneResponse {
  success: boolean
  message: string
  data: {
    user: User
  }
}

// Note: Reactivation happens automatically on backend when deleted user logs in
// Backend sets deletedAt to null during login - no separate API call needed
